import type { ChatMessage } from '../types/chat'
import { parseOrdinalChoice } from './patientOptionOrdinal'

function asciiFold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/**
 * Voice / typed intent: read the **existing** in-chat unread list aloud (AWS Polly).
 *
 * Initial list commands (“read my unread messages”, “read me my messages”, “read out my inbox”, …)
 * are handled by the backend UnreadMessages path (same as “show my unread messages”). Callers must
 * only invoke this after ``findPendingUnreadReadAloudMessageId`` finds a list row.
 */
export function parseUnreadReadAloudIntent(text: string): boolean {
  const t = asciiFold((text || '').trim())
  if (!t) return false
  if (/\b(don't|do not|dont|ne pas|pas besoin|no need)\b.*\b(read|lire|lis)\b/.test(t)) return false
  if (/\b(ne|pas)\s+lis\b/.test(t)) return false

  // Same as backend unread_messages_intent — fetch list first, not read-aloud-only.
  // When the user adds aloud / voix haute, treat as Polly follow-up (list already on screen).
  const readAloudMarker =
    /\bvoix\s+haute\b/.test(t) ||
    /\bhaute\s+voix\b/.test(t) ||
    /\baloud\b/.test(t) ||
    /\bout\s+loud\b/.test(t)

  if (
    !readAloudMarker &&
    (/\bread\s+out\s+my\s+(messages?|inbox)\b/.test(t) ||
    /\bread\s+out\s+the\s+(messages?|inbox)\b/.test(t) ||
    /\bread\s+me\s+my\s+messages\b/.test(t) ||
    /\bread\s+my\s+messages\b/.test(t) ||
    /\bread\s+the\s+messages\s+for\s+me\b/.test(t) ||
    /\bplease\s+read\s+my\s+messages\b/.test(t) ||
    /\bread\s+my\s+unread\b/.test(t) ||
    /\bread\s+my\s+undread\b/.test(t) ||
    /\blis\s+mes\s+messages\b/.test(t) ||
    /\blisez\s+mes\s+messages\b/.test(t) ||
    /\blire\s+mes\s+messages\b/.test(t))
  ) {
    return false
  }

  const frReadAloud =
    /\blire\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blisez\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blis\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blit\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blecture\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blire\s+(?:les\s+|mes\s+)?messages?\s+(?:a\s+)?voix\s+haute\b/.test(t) ||
    /\blire\s+(?:les\s+|mes\s+)?messages?\s+a\s+haute\s+voix\b/.test(t)

  /** Short EN follow-ups after the in-chat unread/urgent list is already visible. */
  const enReadAloud =
    /\bread\s+it\s+for\s+me\b/.test(t) ||
    /\bread\s+them\s+for\s+me\b/.test(t) ||
    /\bread\s+it\s+aloud\b/.test(t) ||
    /\bread\s+them\s+aloud\b/.test(t) ||
    /\bread\s+out\s+loud\b/.test(t) ||
    /\bread\s+aloud\b/.test(t) ||
    /\bread\s+these\s+messages\b/.test(t) ||
    /\bread\s+the\s+messages\b/.test(t) ||
    /\bread\s+the\s+messaging\b/.test(t) ||
    /\bread\s+those\b/.test(t) ||
    /\bread\s+(it|them|this|these)\b/.test(t) ||
    /\bnarrate\s+(the\s+)?(messages|messaging)\b/.test(t) ||
    /\bplay\s+(it|them)\b/.test(t) ||
    /\blisten\s+(?:to\s+)?(it|them|the\s+messages?)\b/.test(t) ||
    /\bhear\s+(it|them)\b/.test(t) ||
    /\bspeak\s+(it|them|this|these)\b/.test(t)

  /** Short FR follow-ups (lis/le/les, écoute, dis-le, …). */
  const frShortRead =
    /\blis[\s-]?moi\b/.test(t) ||
    /\blis[\s-]?le\b/.test(t) ||
    /\blis[\s-]?la\b/.test(t) ||
    /\blis[\s-]?les\b/.test(t) ||
    /\blis[\s-]?ça\b/.test(t) ||
    /\blis[\s-]?ca\b/.test(t) ||
    /\blisez[\s-]?le\b/.test(t) ||
    /\blisez[\s-]?les\b/.test(t) ||
    /\blire[\s-]?le\b/.test(t) ||
    /\blire[\s-]?les\b/.test(t) ||
    /\blire\s+(les\s+)?(messages|message)\b/.test(t) ||
    /\blecture\s+des\s+messages\b/.test(t) ||
    /\bdis[\s-]?le\b/.test(t) ||
    /\bdis[\s-]?les\b/.test(t) ||
    /\becoute(?:r)?[\s-]+(?:les|le|la|ca|ça)\b/.test(t) ||
    /\bjoue[\s-]?les\b/.test(t)

  return enReadAloud || frReadAloud || frShortRead
}

export function parseUnreadThreadOpenIndex(text: string): number | null {
  const t = asciiFold((text || '').trim())
  if (!t) return null
  const rewritten = t
    .replace(/\b(unread|urgent)\b/g, '')
    .replace(/\bmessages?\b/g, ' option')
    .replace(/\binbox\b/g, ' option')
    .replace(/\s+/g, ' ')
    .trim()
  return parseOrdinalChoice(rewritten)
}

/** Latest assistant row that shows in-chat unread list or single last-message card. */
export function findPendingUnreadReadAloudMessageId(messages: readonly ChatMessage[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'assistant' && (m.unreadMessagesUi || m.getLastMessageUi)) {
      return m.id
    }
  }
  return undefined
}
